import { SITE_URL } from "./social";
import { ORGANIZATION_LOGO, SOCIAL_PROFILES } from "./org-schema";

export type Faq = { q: string; a: string };

/**
 * Speakable selectors for voice assistants and AI answer engines:
 * the page H1 and the opening summary paragraph.
 */
export const SPEAKABLE = {
  "@type": "SpeakableSpecification",
  cssSelector: ["h1", ".lead", ".article-summary"],
  xpath: ["/html/head/title", "/html/head/meta[@name='description']/@content"],
};

/** Named, linkable author entity for authorship weighting. */
export const ARTICLE_AUTHOR = {
  "@type": "Organization",
  "@id": `${SITE_URL}/about#editorial-team`,
  name: "LifeWestWay Editorial Team",
  url: `${SITE_URL}/about`,
  sameAs: SOCIAL_PROFILES,
};

export const ARTICLE_PUBLISHER = {
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: "LifeWestWay",
  url: SITE_URL,
  logo: { "@type": "ImageObject", url: ORGANIZATION_LOGO },
};

type ArticleLdInput = {
  url: string;
  headline: string;
  description: string;
  image?: string | string[];
  datePublished: string;
  dateModified?: string;
  section?: string;
  wordCount?: number;
  about?: string;
};

/** Article schema with author (name + URL), dateModified and speakable spec. */
export function buildArticleLd({
  url,
  headline,
  description,
  image,
  datePublished,
  dateModified,
  section,
  wordCount,
  about,
}: ArticleLdInput) {
  const images = image ? (Array.isArray(image) ? image : [image]) : [ORGANIZATION_LOGO];
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${url}#article`,
    headline,
    name: headline,
    description,
    image: images,
    datePublished,
    dateModified: dateModified ?? datePublished,
    inLanguage: "en",
    isAccessibleForFree: true,
    author: ARTICLE_AUTHOR,
    creator: ARTICLE_AUTHOR,
    publisher: ARTICLE_PUBLISHER,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    speakable: SPEAKABLE,
    ...(section ? { articleSection: section } : {}),
    ...(wordCount ? { wordCount } : {}),
    ...(about ? { about: { "@type": "Thing", name: about } } : {}),
  };
}

const OFFICIAL_BODIES: Record<string, { immigration: string; tax: string; label: string }> = {
  usa: { immigration: "USCIS", tax: "the IRS", label: "the United States" },
  uk: { immigration: "UK Visas and Immigration (UKVI)", tax: "HMRC", label: "the United Kingdom" },
  australia: { immigration: "the Department of Home Affairs", tax: "the ATO", label: "Australia" },
  canada: { immigration: "IRCC", tax: "the CRA", label: "Canada" },
  italy: {
    immigration: "the Ministero degli Affari Esteri and your local Questura",
    tax: "Agenzia delle Entrate",
    label: "Italy",
  },
  portugal: { immigration: "AIMA", tax: "Autoridade Tributária", label: "Portugal" },
};

const GLOBAL_BODY = {
  immigration: "the immigration authority of your destination country",
  tax: "your destination country's tax authority",
  label: "your destination country",
};

/**
 * Guarantees at least 5 visible Q&A pairs per guide so the rendered FAQ and
 * the FAQPage schema stay identical. Padding answers are generic but factual.
 */
export function ensureFaq(
  items: Faq[] | undefined,
  ctx: { title: string; country?: string; category?: string; updatedAt?: string },
): Faq[] {
  const base = [...(items ?? [])];
  const body = (ctx.country && OFFICIAL_BODIES[ctx.country]) || GLOBAL_BODY;
  const topic = ctx.category ? ctx.category.toLowerCase() : "relocation";

  const fallbacks: Faq[] = [
    {
      q: `When was this guide last updated?`,
      a: `This guide was last reviewed and updated on ${ctx.updatedAt ?? "its published date"} by the LifeWestWay Editorial Team. We re-check fees, thresholds and processing times against primary sources whenever the underlying rules change.`,
    },
    {
      q: `Where can I confirm these ${body.label} rules officially?`,
      a: `Always confirm current requirements with ${body.immigration} for immigration matters and ${body.tax} for tax matters. Official sources take precedence over any summary, including ours.`,
    },
    {
      q: `Who writes LifeWestWay's ${topic} guides?`,
      a: `Every guide is researched and written by the LifeWestWay Editorial Team. We work from government publications, official fee schedules and published statistics rather than second-hand blog summaries.`,
    },
    {
      q: `Is this legal, immigration or financial advice?`,
      a: `No. LifeWestWay is an independent information publisher, not a law firm, migration agency or financial advisor. For case-specific decisions, consult a licensed immigration lawyer, registered migration agent or qualified accountant in ${body.label}.`,
    },
    {
      q: `How often do the figures in this guide change?`,
      a: `Government fees, income thresholds and processing times are typically revised at least once a year, and sometimes mid-year. We monitor announcements and update affected guides, but always verify a figure before you pay or file.`,
    },
    {
      q: `Can I use this guide if my situation is different?`,
      a: `Use it as a map, not a ruling. The steps and documents described here apply to the common case; dependants, prior refusals, dual nationality and self-employment can all change what you must submit.`,
    },
  ];

  const seen = new Set(base.map((f) => f.q.trim().toLowerCase()));
  for (const f of fallbacks) {
    if (base.length >= 5) break;
    if (seen.has(f.q.trim().toLowerCase())) continue;
    base.push(f);
    seen.add(f.q.trim().toLowerCase());
  }
  return base;
}

/** FAQPage schema (5+ Q&A pairs) with speakable question/answer selectors. */
export function buildFaqLd(items: Faq[], url?: string) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    ...(url ? { "@id": `${url}#faq`, url } : {}),
    inLanguage: "en",
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: ["#faq", "[aria-label='Frequently asked questions']"],
    },
    mainEntity: items.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

/** WebPage node carrying the speakable spec for non-article pages. */
export function buildSpeakableWebPageLd(input: {
  url: string;
  name: string;
  description: string;
  dateModified?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${input.url}#webpage`,
    url: input.url,
    name: input.name,
    description: input.description,
    inLanguage: "en",
    isPartOf: { "@id": `${SITE_URL}/#website` },
    publisher: { "@id": `${SITE_URL}/#organization` },
    speakable: SPEAKABLE,
    ...(input.dateModified ? { dateModified: input.dateModified } : {}),
  };
}
